import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';

import { useFeatureFlags } from 'src/context/flagsContext';
import { useDeepLinkNavigation } from 'src/hooks/useDeepLinkNavigation';
import { useDesktopAppRedirect } from 'src/hooks/useDesktopAppRedirect';
import { getSafeElectronAPI, isElectron } from 'src/hooks/useElectron';
import { useElectronUpdates } from 'src/hooks/useElectronUpdates';
import { useNativeNotifications } from 'src/hooks/useNativeNotifications';
import { useNotificationNavigation } from 'src/hooks/useNotificationNavigation';

import { DesktopAppInterstitial } from './DesktopAppInterstitial';
import { initElectronUpdateStore } from './ElectronUpdateContext';

/**
 * Component that initializes all Electron-specific integrations.
 * This should be rendered once at the app level.
 *
 * Integrations:
 * - Badge count sync: Updates the dock badge with unread notification count
 * - Notification navigation: Handles clicks on native notifications
 * - Desktop app redirect: Redirects to the desktop app when a "deep" link is clicked
 * - Update store: Subscribes to IPC update events via module-level external store
 */
export const ElectronIntegration: React.FC = () => {
  // Skip electron-specific hooks if not in Electron
  // but include the components required for Electron features that should
  // run in the browser (e.g. DesktopAppRedirect)
  if (!isElectron()) {
    return <ElectronIntegrationOuter />;
  }

  return <ElectronIntegrationInner />;
};

/**
 * Inner component that actually calls the hooks.
 * Separated to ensure hooks are only called when in Electron environment.
 */
const ElectronIntegrationInner: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { EnableNewSidebar } = useFeatureFlags();

  // Initialize the module-level update store with IPC listeners
  useEffect(() => {
    return initElectronUpdateStore(getSafeElectronAPI());
  }, []);

  // Sync unread notification count to dock badge
  useNativeNotifications();

  // Handle clicks on native notifications
  useNotificationNavigation();

  // Handle deep link navigation (assembly:// protocol)
  useDeepLinkNavigation();

  // Show toast when a desktop app update is downloaded
  useElectronUpdates(enqueueSnackbar, EnableNewSidebar);

  return null;
};

/**
 * Outer component that actually calls the hooks.
 *
 * These are hooks that should not run in the Electron environment,
 * but should run in the browser environment to enable a specific feature
 * (e.g. DesktopAppRedirect).
 */
const ElectronIntegrationOuter: React.FC = () => {
  const { isRedirecting, dismiss, retryDeepLink } = useDesktopAppRedirect();

  if (isRedirecting) {
    return (
      <DesktopAppInterstitial
        onDismiss={dismiss}
        onRetryDeepLink={retryDeepLink}
      />
    );
  }

  return null;
};
